import { useAppContext } from "@/context/AppContext";
import {
  Address,
  AssetId,
  CredentialType,
  Metadata,
  MetadatumList,
  MetadatumMap,
  Metadatum,
  TokenMap,
  TransactionId,
  TransactionInput,
  TransactionOutput,
  TransactionUnspentOutput,
  Value,
} from "@blaze-cardano/core";
import { TPlutusData, Type, serialize } from "@blaze-cardano/data";
import { ensureEphemeralWallet, signWithEphemeralWallet } from "@/lib/ephemeralWallet";
import { markTransactionInputsInFlight, filterInFlightUtxos } from "@/lib/inFlightUtxos";
import { publicKeyFromCoseKey } from "@/lib/cose";
import { blake2b } from "@noble/hashes/blake2";
import { AssetAmount } from "@sundaeswap/asset";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { DefaultError, useMutation } from "@tanstack/react-query";
import { useDepositWithBody } from "./useDepositWithBody";
import { useHydrozoaHeadInfo } from "./useHydrozoaScriptAddress";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { useDepositTxs } from "@/context/GummiwormContext/hooks/useDepositTxs";

import { randomBytes } from '@noble/hashes/utils.js';
import { chacha20poly1305 } from "@noble/ciphers/chacha.js";

const CredentialSchema = Type.Union([
  Type.Object(
    { PubKeyCredential: Type.Object({ hash: Type.String() }, { ctor: 0 }) },
    { ctor: 0 },
  ),
  Type.Object(
    { ScriptCredential: Type.Object({ hash: Type.String() }, { ctor: 1 }) },
    { ctor: 1 },
  ),
]);

const PlutusAddressSchema = Type.Object(
  {
    paymentCredential: CredentialSchema,
    stakeCredential: Type.Optional(
      Type.Union([
        Type.Object(
          { Inline: CredentialSchema },
          { ctor: 0 },
        ),
        Type.Object(
          {
            Pointer: Type.Tuple([
              Type.Object({
                slotNumber: Type.BigInt(),
                transactionIndex: Type.BigInt(),
                certificateIndex: Type.BigInt(),
              }),
            ]),
          },
          { ctor: 1 },
        ),
      ]),
    ),
  },
  { ctor: 0 },
);

const RefundInstructionsSchema = Type.Object(
  {
    Address: PlutusAddressSchema,
    MbDatum: Type.Optional(TPlutusData),
    RefundStart: Type.BigInt(),
  },
  { additionalProperties: false, ctor: 0 },
);

const DepositUtxoDatumSchema = Type.Object(
  {
    RefundInstructions: RefundInstructionsSchema,
  },
  { additionalProperties: false, ctor: 0 },
);

const toSeconds = (value: number) => Math.floor(value / 1000);

export const useHandleDepositRequest = () => {
  const {
    state: { blaze, provider, orderEncryptionKey },
  } = useAppContext();
  const { mainAddress } = useWalletObserver();
  const { depositTxs, addDepositTx, updateDepositTx } = useDepositTxs();
  const headInfo = useHydrozoaHeadInfo();
  const { mutateAsync: deposit } = useDepositWithBody();

  return useMutation<unknown, DefaultError, { depositAmounts: AssetAmount[] }>({
    mutationKey: [
      "deposit",
      "register",
      blaze,
      provider,
      mainAddress,
      headInfo,
      addDepositTx,
    ],
    mutationFn: async ({ depositAmounts }) => {
      if (!blaze || !mainAddress || !provider) {
        throw new Error(
          "Connect a Cardano wallet before registering a deposit.",
        );
      }

      if (!headInfo) {
        throw new Error(
          "Sugar Rush is offline right now. Please try again later.",
        );
      }

      const { headAddress: scriptAddress, headId, multisigRegimeUtxo, submissionDurationSeconds, refundStartOffsetSeconds, absorptionStartOffsetSeconds } = headInfo;

      const refundAddress = Address.fromBech32(mainAddress);
      const props = refundAddress.getProps();
      const paymentHash = props.paymentPart?.hash;
      if (!paymentHash) {
        throw new Error("Could not extract payment credential from address.");
      }
      const paymentCredential =
        props.paymentPart!.type === CredentialType.ScriptHash
          ? { ScriptCredential: { hash: paymentHash } }
          : { PubKeyCredential: { hash: paymentHash } };
      // Force enterprise address (no staking credential) for now
      const stakeCredential = undefined;

      const validityStart = toSeconds(Date.now() - 1_000);
      // A block whose creation time falls inside [validityStart, validityEnd] must exist for
      // the deposit to be absorbed. Blocks are paced by hydrozoa's softBlockMinPeriod (20s in
      // the demo head) plus consensus latency, so a ~30s window was racy — give it ~2 minutes.
      const validityEnd = validityStart + 121;
      const refundStart = validityEnd + refundStartOffsetSeconds;
      const txValidUntil = validityEnd + submissionDurationSeconds;
      const datum = serialize(DepositUtxoDatumSchema, {
        RefundInstructions: {
          Address: { paymentCredential, stakeCredential },
          MbDatum: undefined,
          RefundStart: BigInt(refundStart * 1_000),
        },
      });

      // Get the ephemeral key so we can register it as a delegated key
      const ephemeral = await ensureEphemeralWallet(mainAddress);

      const ephemeralKeyHash = Buffer.from(blake2b(
        Buffer.from(ephemeral.publicKey, "hex"),
        { dkLen: 28 },
      )).toString("hex");

      const l2Payload = {
        accountId: Address.fromBech32(mainAddress).getProps().paymentPart?.hash,
      };

      let sessionKey = orderEncryptionKey.session.publicKey;
      if (sessionKey.length != 32) {
        throw new Error("Incorrect size for session key");
      }
      let hkdfSalt = orderEncryptionKey.hkdfSalt;
      if (hkdfSalt.length != 32) {
        throw new Error("Incorrect size for hkdf salt");
      }
      let encryptionKey = orderEncryptionKey.encryption;
      const nonce = randomBytes(12);
      const chacha = chacha20poly1305(encryptionKey, nonce);
      let l2PayloadBuf = Buffer.from(JSON.stringify(l2Payload));
      let l2PayloadBufEnc = chacha.encrypt(l2PayloadBuf);
      let bufLength = sessionKey.length + hkdfSalt.length + nonce.length + l2PayloadBufEnc.length;
      let l2PayloadBufBytes = new Uint8Array(bufLength);
      l2PayloadBufBytes.set(sessionKey, 0);
      l2PayloadBufBytes.set(hkdfSalt, sessionKey.length)
      l2PayloadBufBytes.set(nonce, sessionKey.length + hkdfSalt.length);
      l2PayloadBufBytes.set(l2PayloadBufEnc, sessionKey.length + hkdfSalt.length + nonce.length);

      let l2PayloadHash = Buffer.from(blake2b(
        Buffer.from(l2PayloadBufBytes),
        { dkLen: 32 },
      )).toString("hex");

      const depositDetails = new MetadatumMap();
      depositDetails.insert(
        Metadatum.newText("depositIx"),
        Metadatum.newInteger(0n),
      );
      depositDetails.insert(
        Metadatum.newText("depositFee"),
        Metadatum.newInteger(0n),
      );

      let fromHex = (hex) => {
        return Uint8Array.fromHex(hex);
      };

      depositDetails.insert(
        Metadatum.newText("l2PayloadHash"),
        Metadatum.newBytes(fromHex(l2PayloadHash)),
      );

      const headIdMap = new MetadatumMap();
      headIdMap.insert(
        Metadatum.newText(headId),
        Metadatum.newMap(depositDetails),
      );

      const depositMap = new MetadatumMap();
      depositMap.insert(
        Metadatum.newText("Deposit"),
        Metadatum.newMap(headIdMap),
      );

      const metadata = new Metadata(
        new Map([[4937n, Metadatum.newMap(depositMap)]]),
      );

      // Build reference input for multisig regime UTxO
      // We construct a minimal UTxO rather than resolving via provider,
      // because the regime UTxO contains a native script ref that Blaze
      // can't deserialize. Only the input ref matters for reference inputs.
      const regimeInput = new TransactionInput(
        TransactionId(multisigRegimeUtxo.transaction_id) as any,
        BigInt(multisigRegimeUtxo.index),
      );
      const regimeOutput = new (TransactionOutput as any)(
        Address.fromBech32(scriptAddress),
        new Value(0n),
      );
      const regimeUtxo = new TransactionUnspentOutput(regimeInput, regimeOutput);

      // Build Value from all deposit amounts
      let lovelace = 0n;
      const tokens = new Map() as TokenMap;
      for (const amt of depositAmounts) {
        if (amt.metadata.assetId === "ada.lovelace") {
          lovelace += amt.amount;
        } else {
          // Convert sundae assetId format "policyId.assetNameHex" to blaze AssetId
          const [policyId, assetName] = amt.metadata.assetId.split(".");
          const blazeAssetId = AssetId(policyId + (assetName ?? ""));
          tokens.set(blazeAssetId, (tokens.get(blazeAssetId) ?? 0n) + amt.amount);
        }
      }
      const depositValue = new Value(lovelace, tokens.size > 0 ? tokens : undefined);

      // lockAssets is called first so the deposit is always output index 0.
      // We add a preCompleteHook that filters out UTxOs already consumed by
      // in-flight deposits so rapid successive deposits don't double-spend.
      const tx = await blaze
        .newTransaction()
        .addPreCompleteHook(async (txBuilder) => {
          const utxoSet = (txBuilder as any).utxos as Set<TransactionUnspentOutput>;
          const filtered = filterInFlightUtxos([...utxoSet]);
          utxoSet.clear();
          for (const u of filtered) utxoSet.add(u);
        })
        .setFeePadding(10000n)
        .setValidUntil(provider.unixToSlot(txValidUntil * 1_000))
        .lockAssets(
          Address.fromBech32(scriptAddress),
          depositValue,
          datum,
        )
        .addReferenceInput(regimeUtxo)
        .setMetadata(metadata)
        .complete();

      // Mark the inputs consumed by this tx so subsequent deposits avoid them.
      // Expires at the tx TTL (txValidUntil is in seconds) + 30s buffer.
      markTransactionInputsInFlight(tx, txValidUntil * 1_000);

      let l1Payload = tx.toCbor();

      const body = {
        type: "deposit",
        l1Payload,
        l2Payload: l2PayloadBufBytes.toHex(),
      };

      const result = await deposit(body);

      const id = BigInt(result.requestId);

      // Estimate when the deposit will be absorbed:
      // validityEnd + absorptionStartOffsetSeconds
      const estimatedAbsorptionTime = absorptionStartOffsetSeconds
        ? validityEnd + absorptionStartOffsetSeconds
        : undefined;

      // Register the deposit before signing so we can track status
      // even if the refund tx arrives before signing completes.
      if (!depositTxs.has(id.toString())) {
        addDepositTx(id, {
          cbor: "",
          hash: "",
          refundCbor: "",
          status: "new",
          estimatedAbsorptionTime,
        });
      }

      const signedTx = await blaze.signTransaction(tx);
      const cbor = signedTx.toCbor();

      // Save the signed CBOR. The reconciler in GummiwormProvider will
      // detect cbor + refundCbor are both present and handle submission.
      updateDepositTx(id, { cbor });

      return result;
    },
  });
};
